import { type Dispatch, type SetStateAction } from "react"
import {
  CaseReducer,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit"

import { toastManager } from "@/lib/toast"
import { assert } from "@/lib/utils/assertions"
import { empty } from "@/lib/utils/presence"
import { customerMessageToAmplitudeProperties } from "../analytics/amplitude-properties"
import { sendAnalyticsEvent } from "../analytics/amplitude-utils"
import { formatList } from "../format-utils"
import { tripDayCount } from "../trip-utils"
import { selectHasUnscheduledEvents } from "./itinerary-slice"
import { selectLocale } from "./locale-selectors"
import { sendOrEditMessage } from "./message-thunks"
import { AppDispatch, RootState } from "./redux-store"
import { selectTrip } from "./trip-slice"
import userSlice from "./user-slice"

// A state machine governing the status of itinerary generation.
// A canned generation request sends a message to the bot and then receives
// both the response and inline itinerary events in the stream. State goes from
//    idle
//      --[send canned customer question to the server*]-->
//    streaming
//      --[stream completes]-->
//    idle
//

export interface ItineraryGenerationSliceState {
  currentState: MachineState
}

export const itineraryGenerationSliceInitialState: ItineraryGenerationSliceState =
  {
    currentState: { status: "idle" },
  }
type MachineState = IdleMachineState | StreamingMachineState

interface IdleMachineState {
  status: "idle"
}

interface StreamingMachineState {
  status: "streaming"
}

const selectSliceState = (state: RootState) => state.itineraryGenerationSlice

export const selectItineraryGenerationMachineState = createSelector(
  [selectSliceState],
  (state) => state.currentState
)

export const selectIsGeneratingItinerary = createSelector(
  [selectItineraryGenerationMachineState],
  (itineraryGenerationState) => itineraryGenerationState.status !== "idle"
)

const setIdle: CaseReducer<ItineraryGenerationSliceState> = (state) => {
  state.currentState = { status: "idle" }
}

const setStreaming: CaseReducer<ItineraryGenerationSliceState> = (state) => {
  assert(state.currentState.status === "idle", "status should be idle")
  state.currentState = { status: "streaming" }
}

// Sends a customer message asking the bot for an itinerarary and
// monitors the response for itinerary events.
export const askBotToGenerateItinerary = createAsyncThunk<
  void,
  | {
      setExpanded?: Dispatch<SetStateAction<boolean>>
      includeVibes?: boolean
    }
  | undefined,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "itineraryGeneration/askBotToGenerateItinerary",
  async (args, { dispatch, getState }) => {
    args ||= {}
    const { setExpanded } = args
    const includeVibes = args.includeVibes ?? true
    try {
      const startingState = selectItineraryGenerationMachineState(getState())
      assert(startingState.status === "idle", "status should be idle")

      const trip = selectTrip(getState())
      if (!trip) {
        throw new Error("No trip found")
      }
      const dayCount = tripDayCount(trip)
      const hasUnscheduledEvents = selectHasUnscheduledEvents(getState())

      if (setExpanded) setExpanded(false)

      const vibePhrase =
        includeVibes && !empty(trip.vibes)
          ? formatList(
              selectLocale(getState()),
              (trip.vibes || []).map((v) => `"${v.title}"`)
            )
          : undefined

      const messageString = hasUnscheduledEvents
        ? `${trip.collaboration ? "@mindtrip " : ""}Organize my existing trip ideas into a structured ${dayCount}-day itinerary. Each day should have a title, a hotel, restaurants, and things to do.`
        : `${trip.collaboration ? "@mindtrip " : ""}Build a detailed day-by-day itinerary for a ${dayCount}-day trip, where each day has a title and some suggestions. I’ll need a hotel, restaurants, and things to do.`
      const hint = vibePhrase && `The trip should have ${vibePhrase} vibes.`

      const message = await dispatch(
        sendOrEditMessage({ messageString, hint })
      ).unwrap()
      if (message) {
        sendAnalyticsEvent({
          name: "AIItineraryGenerate",
          properties: customerMessageToAmplitudeProperties(message),
        })
      }
    } catch (error) {
      console.error("Error generating itinerary: ", error)
      dispatch(itineraryGenerationSlice.actions.setIdle())
      toastManager.add({
        type: "error",
        description: "Failed to generate itinerary.",
      })
    }
  }
)

// Experimental feature: Sends a customer message asking the bot for a road trip itinerary
export const askBotToGenerateRoadTrip = createAsyncThunk<
  void,
  {
    start: string
    destination: string
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "itineraryGeneration/askBotToGenerateRoadTrip",
  async (args, { dispatch, getState }) => {
    try {
      const startingState = selectItineraryGenerationMachineState(getState())
      assert(startingState.status === "idle", "status should be idle")

      const trip = selectTrip(getState())
      if (!trip) {
        throw new Error("No trip found")
      }
      const dayCount = tripDayCount(trip)
      const start = args.start
      const destination = args.destination

      const messageString = `Build a detailed itinerary for a road trip from ${start} to ${destination}.`
      let hint = `The trip is ${dayCount} days, where each day has a title and suggested stops and lodging. Summarize the day count, budget and theme in your response.`
      if (trip.budget) {
        hint += `The trip should have ${trip.budget} budget. `
      }
      if (!empty(trip.vibes)) {
        const vibePhrase = formatList(
          selectLocale(getState()),
          (trip.vibes || []).map((v) => `"${v.title}"`)
        )
        hint += `The trip should have a theme of ${vibePhrase}. `
      }

      const message = await dispatch(
        sendOrEditMessage({ messageString, hint })
      ).unwrap()
      if (message) {
        sendAnalyticsEvent({
          name: "AIItineraryGenerate",
          properties: customerMessageToAmplitudeProperties(message),
        })
      }
    } catch (error) {
      console.error("Error generating itinerary: ", error)
      dispatch(itineraryGenerationSlice.actions.setIdle())
      toastManager.add({
        type: "error",
        description: "Failed to generate itinerary.",
      })
    }
  }
)

const itineraryGenerationSlice = createSlice({
  name: "itineraryGeneration",
  initialState: itineraryGenerationSliceInitialState,
  reducers: {
    setIdle,
    setStreaming,
  },

  // Listen for actions that were dispatched to other slices and
  // take related action here in the itinerary generation slice.
  extraReducers: (builder) => {
    builder
      // If we see itinerary events in the in-progress message we're
      // receiving from the bot, switch into streaming mode.
      .addCase(userSlice.actions.updateInProgressMessage, (state, action) => {
        const { message } = action.payload
        if (
          state.currentState.status === "idle" &&
          message.isGeneratingItinerary
        ) {
          setStreaming(state, { type: action.type })
        }
      })
      // When message streaming completes, see if we're in the middle
      // of itinerary generation and advance the state machine accordingly.
      .addCase(userSlice.actions.stopMessageStreaming, (state, action) => {
        const status = state.currentState.status
        if (status === "streaming") {
          setIdle(state, { type: action.type })
        }
      })
      .addCase(
        userSlice.actions.stopMessageStreamingAndDisable,
        (state, action) => {
          const status = state.currentState.status
          if (status === "streaming") {
            setIdle(state, { type: action.type })
          }
        }
      )
      .addCase(
        userSlice.actions.stopMessageStreamingWithCancel,
        (state, action) => {
          const status = state.currentState.status
          if (status === "streaming") {
            setIdle(state, { type: action.type })
          }
        }
      )
      .addCase(
        userSlice.actions.stopMessageStreamingWithError,
        (state, action) => {
          const status = state.currentState.status
          if (status === "streaming") {
            setIdle(state, { type: action.type })
          }
        }
      )
  },
})

export type itineraryGenationSlice = typeof itineraryGenerationSlice
export default itineraryGenerationSlice
